import {defer, EMPTY, interval, merge, NEVER, Observable, timer} from 'rxjs';
import {catchError, filter, ignoreElements, map, publish, scan, share, switchMap} from 'rxjs/operators';

import {globalWindow} from '../../helpers/browser/browserHelpers';
import {isDevelopment} from '../../helpers/environment/envHelpers';
import {exhaustMapTrailing} from '../../helpers/rx/exhaustMapTrailing';
import {fromOfflineEvent, fromOnlineEvent} from '../../helpers/rx/reachability';
import {sendAsync, spyOnHttpResponses} from '../http/httpBase';
import {HttpFullSendOptions, RequestMethodsEnum} from '../http/httpTypes';

/*
 * Constants.
 */

/** Allow up to this many network errors in sequence before declaring we're offline. */
const defaultMaxErrorCount = 2;

/** Ping at this interval when we've seen at least one network error. */
const defaultIndeterminatePingInterval = 5000;

/** Ping at this interval when we're offline. */
const defaultOfflinePingInterval = 10000;

/*
 * API.
 */

export interface NetworkMonitor {
  readonly isOnline$: Observable<boolean>;
}

export const networkMonitor: NetworkMonitor = createDefaultNetworkMonitor();

/*
 * Helpers.
 */

let pingPath: string | undefined;

export function setupNetworkMonitor(path: string) {
  pingPath = path;
}

async function pingAsync() {
  if (!pingPath) {
    if (isDevelopment()) {
      // eslint-disable-next-line no-console
      console.error('NetworkMonitor pingPath is not set. Call setupNetworkMonitor with a ping endpoint.');
    }
    return;
  }

  await sendAsync({
    method: RequestMethodsEnum.GET,
    path: pingPath,
  });
}

function isPingOptions(options: HttpFullSendOptions | undefined): boolean {
  return options?.path === pingPath;
}

function createDefaultNetworkMonitor(): NetworkMonitor {
  // Poke at the network when we see online/offline events.
  const extraPingTriggers$ = merge(fromOnlineEvent(globalWindow), fromOfflineEvent(globalWindow));

  // Spy on HTTP requests to determine network connectivity.
  const pongs$ = new Observable<boolean>((subscriber) =>
    spyOnHttpResponses(
      () => subscriber.next(true),
      (options) => {
        if (!isPingOptions(options)) {
          return;
        }

        // Only use ping requests to decide we're offline.
        subscriber.next(false);
      },
    ),
  );

  return createNetworkMonitor({
    pingAsync,
    indeterminatePingInterval: defaultIndeterminatePingInterval,
    offlinePingInterval: defaultOfflinePingInterval,
    extraPingTriggers$,
    pongs$,
    maxErrorCount: defaultMaxErrorCount,
  });
}

interface NetworkMonitorOptions {
  pingAsync: () => Promise<void>;
  indeterminatePingInterval: number;
  offlinePingInterval: number;
  extraPingTriggers$: Observable<unknown>;
  pongs$: Observable<boolean>;
  maxErrorCount: number;
}

export function createNetworkMonitor(options: NetworkMonitorOptions): NetworkMonitor {
  return {
    isOnline$: createIsOnlineWithPings(options),
  };
}

function createIsOnlineWithPings(options: NetworkMonitorOptions): Observable<boolean> {
  // Turn options.pingAsync into an empty observable.
  const onePing$ = defer(() => options.pingAsync()).pipe(
    ignoreElements(),
    catchError(() => EMPTY),
  );

  return options.pongs$.pipe(
    // Count sequential network errors.
    scan((errorCount, isResponse) => (isResponse ? 0 : errorCount + 1), 0),
    publish((errorCount$) => {
      // Derive our state from the error count.
      const isOnline$ = errorCount$.pipe(
        // Filter out inconclusive error counts.
        filter((errorCount) => errorCount === 0 || errorCount > options.maxErrorCount),
        map((errorCount) => errorCount <= options.maxErrorCount),
      );

      // Trigger a ping each time the error count changes and is inconclusive. We assume the result will feed back into
      // the error count.
      const indeterminatePingTriggers$ = errorCount$.pipe(
        map((errorCount) => errorCount > 0 && errorCount <= options.maxErrorCount),
        switchMap((isIndeterminate) =>
          isIndeterminate ? timer(0, options.indeterminatePingInterval) : NEVER,
        ),
      );

      // Ping periodically while offline.
      const offlinePingTriggers$ = errorCount$.pipe(
        map((errorCount) => errorCount > options.maxErrorCount),
        switchMap((isOffline) => (isOffline ? interval(options.offlinePingInterval) : NEVER)),
      );

      // Create an observable to trigger all pings.
      const allPingTriggers$ = merge(
        indeterminatePingTriggers$,
        offlinePingTriggers$,
        options.extraPingTriggers$,
      );

      // Perform the ping, but allow only one ping at a time.
      const allPings$ = allPingTriggers$.pipe(
        exhaustMapTrailing(() => onePing$),
        ignoreElements(),
      );

      return merge(isOnline$, allPings$);
    }),
    share(),
  );
}
